import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Loader2, Plug, RefreshCw, ShieldCheck, Save, ListTree, ScrollText, Cloud,
  CheckCircle2, AlertTriangle, Copy, Check, Users, Calendar, FolderOpen, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm";
const RES_ICON = { users: Users, groups: Users, teams: MessageSquare, calendar: Calendar, files: FolderOpen };

export default function Microsoft365() {
  const [cfg, setCfg] = useState(null);
  const [guide, setGuide] = useState(null);
  const [form, setForm] = useState({ client_id: "", tenant_id_ms: "", client_secret: "", sandbox: true, sso_enabled: true });
  const [datasets, setDatasets] = useState([]);
  const [logs, setLogs] = useState([]);
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [adminAllowed, setAdminAllowed] = useState(true);
  const [copied, setCopied] = useState(false);
  const [myConn, setMyConn] = useState(null);
  const [myEvents, setMyEvents] = useState(null);

  const loadMine = useCallback(() => {
    api.get("/m365/connect/status").then((r) => setMyConn(r.data)).catch(() => setMyConn({ connected: false }));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    loadMine();
    Promise.all([api.get("/m365/config"), api.get("/m365/setup-guide"), api.get("/m365/datasets"), api.get("/m365/logs")])
      .then(([c, g, d, l]) => {
        setCfg(c.data); setGuide(g.data); setDatasets(d.data); setLogs(l.data); setAdminAllowed(true);
        setForm((f) => ({ ...f, client_id: c.data.client_id || "", tenant_id_ms: c.data.tenant_id_ms || "",
          sandbox: c.data.connected ? c.data.sandbox : true, sso_enabled: c.data.sso_enabled ?? true }));
      })
      .catch((e) => { if (e.response?.status === 403) setAdminAllowed(false); })
      .finally(() => setLoading(false));
  }, [loadMine]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("connected");
    if (p === "1") { toast.success("¡Tu cuenta de Microsoft quedó conectada!"); loadMine(); window.history.replaceState({}, "", "/os/microsoft"); }
    else if (p === "error") { toast.error("No se pudo conectar tu cuenta de Microsoft. Intenta de nuevo."); window.history.replaceState({}, "", "/os/microsoft"); }
  }, [loadMine]);

  const connectMine = async () => {
    setBusy("connect");
    try { const { data } = await api.get("/m365/connect/start"); window.location.href = data.auth_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo iniciar la conexión"); setBusy(""); }
  };
  const disconnectMine = async () => {
    try { await api.post("/m365/connect/disconnect"); toast.success("Cuenta de Microsoft desconectada"); setMyEvents(null); loadMine(); }
    catch { toast.error("No se pudo desconectar"); }
  };
  const loadMyCalendar = async () => {
    setBusy("cal");
    try { const { data } = await api.get("/m365/me/calendar"); setMyEvents(data.events || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo leer tu calendario"); }
    finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try { await api.put("/m365/config", form); toast.success("Configuración guardada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar"); }
    finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post("/m365/test"); data.ok ? toast.success(data.detail) : toast.error(data.detail); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(""); }
  };
  const sync = async () => {
    setBusy("sync");
    try { const { data } = await api.post("/m365/sync", { resources: [] });
      data.status === "ok" ? toast.success(`Sincronización completa · ${data.total} registros absorbidos`) : toast.error(`Error: ${data.error || ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(""); }
  };
  const openTable = async (res) => {
    try { const { data } = await api.get(`/m365/datasets/${res}?limit=100`); setTable(data); }
    catch { toast.error("No se pudo abrir"); }
  };

  return (
    <OSLayout>
      <div className="max-w-5xl mx-auto pb-24">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-violet text-xs font-medium mb-2"><Cloud className="w-4 h-4" /> MICROSOFT 365 HUB</div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-navy tracking-tight">Microsoft 365 · Integración completa</h1>
          <p className="text-navy/60 mt-2 text-sm max-w-2xl">Conecta OMNIA con tu Microsoft 365 (Entra ID) y absorbe Directorio, Grupos, Calendario, Teams y Archivos. Incluye "Iniciar sesión con Microsoft". Sin credenciales opera en modo sandbox.</p>
        </div>

        {loading ? <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-violet" /></div> : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ ease }} className="space-y-6">
            {/* Conectar MI Microsoft (todos los usuarios) */}
            <div className="rounded-2xl border border-violet/30 bg-violet/[0.03] p-5" data-testid="m365-my-connect">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet/10 text-violet flex items-center justify-center"><Cloud className="w-5 h-5" /></div>
                  <div>
                    <div className="font-medium text-navy">Mi cuenta de Microsoft</div>
                    {myConn?.connected
                      ? <div className="text-xs text-emerald-600">Conectado como {myConn.ms_name || myConn.ms_email}</div>
                      : <div className="text-xs text-navy/50">Conecta tu cuenta para usar tu calendario, correo y Teams desde OMNIA. Tu contraseña se ingresa en Microsoft, OMNIA nunca la ve.</div>}
                  </div>
                </div>
                <div className="flex gap-2">
                  {myConn?.connected ? (
                    <>
                      <button data-testid="m365-my-calendar" onClick={loadMyCalendar} disabled={busy === "cal"} className="text-sm rounded-lg border border-navy/15 px-3 py-2 inline-flex items-center gap-1.5 hover:bg-navy/5 disabled:opacity-50">{busy === "cal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />} Ver mi calendario</button>
                      <button data-testid="m365-my-disconnect" onClick={disconnectMine} className="text-sm rounded-lg border border-rose-200 text-rose-500 px-3 py-2 hover:bg-rose-50">Desconectar</button>
                    </>
                  ) : (
                    <button data-testid="m365-my-connect-btn" onClick={connectMine} disabled={busy === "connect"} className="text-sm rounded-full bg-gradient-violet text-white px-5 py-2.5 inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50">{busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />} Conectar mi Microsoft</button>
                  )}
                </div>
              </div>
              {myEvents && (
                <div className="mt-4 border-t border-navy/10 pt-4" data-testid="m365-my-events">
                  <div className="text-xs uppercase tracking-wide text-navy/40 mb-2">Próximos eventos</div>
                  {myEvents.length === 0 ? <p className="text-sm text-navy/40">Sin eventos próximos.</p> : (
                    <ul className="space-y-1.5">{myEvents.slice(0, 6).map((e, i) => (
                      <li key={i} className="text-sm text-navy/80 flex justify-between gap-3"><span className="truncate">{e.subject || "(sin título)"}</span><span className="text-navy/40 text-xs whitespace-nowrap">{e.start?.dateTime ? new Date(e.start.dateTime).toLocaleString("es-MX") : ""}</span></li>
                    ))}</ul>
                  )}
                </div>
              )}
            </div>

            {!adminAllowed && (
              <p className="text-sm text-navy/40" data-testid="m365-admin-note">La configuración de la conexión de la empresa la administra el liderazgo/TI.</p>
            )}

            {adminAllowed && cfg && (<div className="space-y-6">
            {/* estado */}
            <div className="rounded-2xl border border-navy/10 bg-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid="m365-status">
              <div className="flex items-center gap-3">
                {cfg.status === "conectado" ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : cfg.status === "error" ? <AlertTriangle className="w-6 h-6 text-rose-500" /> : <Cloud className="w-6 h-6 text-violet" />}
                <div>
                  <div className="font-medium text-navy">Estado: {cfg.status}{cfg.sandbox ? " (sandbox)" : ""}</div>
                  <div className="text-xs text-navy/50">{cfg.last_sync ? `Última sync: ${new Date(cfg.last_sync).toLocaleString("es-MX")}` : "Sin sincronizar aún"}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button data-testid="m365-test" onClick={test} disabled={busy} className="text-sm rounded-lg border border-navy/15 px-3 py-2 inline-flex items-center gap-1.5 hover:bg-navy/5 disabled:opacity-50">{busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Probar</button>
                <button data-testid="m365-sync" onClick={sync} disabled={busy} className="text-sm rounded-lg bg-violet text-white px-3 py-2 inline-flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50">{busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sincronizar todo</button>
              </div>
            </div>

            {/* guía de setup */}
            {guide && (
              <details className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="m365-guide">
                <summary className="cursor-pointer font-medium text-navy">📋 Cómo generar las credenciales en Microsoft Entra (para tu TI)</summary>
                <ol className="mt-4 space-y-2 text-sm text-navy/70 list-decimal pl-5">
                  <li>Entra a <b>entra.microsoft.com → Identidad → Registros de aplicaciones → Nuevo registro</b>.</li>
                  <li>Tipo de cuenta: <b>{guide.account_types}</b>.</li>
                  <li>En <b>Autenticación → Agregar plataforma → Web</b>, agrega este Redirect URI exacto:
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-navy/5 rounded px-2 py-1 text-xs break-all">{guide.redirect_uri}</code>
                      <button onClick={() => { navigator.clipboard?.writeText(guide.redirect_uri); setCopied(true); }} className="text-violet">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button>
                    </div>
                  </li>
                  <li>En <b>Certificados y secretos</b> crea un <b>secreto de cliente</b> y copia su <b>Valor</b>.</li>
                  <li>En <b>Permisos de API → Microsoft Graph → Permisos de aplicación</b>, agrega: <code className="text-xs">{guide.permissions.join(", ")}</code>.</li>
                  <li>Pulsa <b>"Conceder consentimiento de administrador"</b>.</li>
                  <li>Comparte de vuelta: <b>Client ID (ID de aplicación)</b>, <b>Tenant ID (ID de directorio)</b> y el <b>Valor del secreto</b>. Pégalos abajo.</li>
                </ol>
              </details>
            )}

            {/* config */}
            <div className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="m365-config">
              <div className="font-medium text-navy mb-4">Credenciales de Entra</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-navy/50 block mb-1">Client ID (ID de aplicación)</label>
                  <input className={input} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="m365-client-id" /></div>
                <div><label className="text-xs text-navy/50 block mb-1">Tenant ID (ID de directorio)</label>
                  <input className={input} value={form.tenant_id_ms} onChange={(e) => setForm({ ...form, tenant_id_ms: e.target.value })} data-testid="m365-tenant-id" /></div>
                <div className="sm:col-span-2"><label className="text-xs text-navy/50 block mb-1">Client Secret (Valor){cfg.client_secret_set && <span className="text-gold ml-1">— guardado, deja vacío para conservar</span>}</label>
                  <input type="password" className={input} value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} placeholder={cfg.client_secret_set ? "••••••" : ""} data-testid="m365-client-secret" /></div>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-4">
                <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer"><input type="checkbox" checked={form.sandbox} onChange={(e) => setForm({ ...form, sandbox: e.target.checked })} data-testid="m365-sandbox" /> Modo sandbox (datos simulados)</label>
                <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer"><input type="checkbox" checked={form.sso_enabled} onChange={(e) => setForm({ ...form, sso_enabled: e.target.checked })} data-testid="m365-sso" /> Habilitar "Iniciar sesión con Microsoft"</label>
                <button data-testid="m365-save" onClick={save} disabled={busy} className="ml-auto rounded-full bg-gradient-violet text-white px-6 py-2.5 text-sm inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>
              </div>
            </div>

            {/* datos absorbidos */}
            <div className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="m365-datasets">
              <div className="flex items-center gap-2 font-medium text-navy mb-4"><ListTree className="w-4 h-4 text-violet" /> Información absorbida</div>
              {datasets.length === 0 ? <p className="text-sm text-navy/40 py-4">Aún no hay datos. Configura y pulsa "Sincronizar todo".</p> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {datasets.map((d) => { const Ic = RES_ICON[d.resource] || ListTree; return (
                    <button key={d.resource} onClick={() => openTable(d.resource)} data-testid={`m365-res-${d.resource}`} className="text-left rounded-xl border border-navy/10 p-4 hover:border-violet transition-colors">
                      <div className="flex items-center gap-2 text-navy"><Ic className="w-4 h-4 text-violet" /><span className="font-medium">{d.label}</span></div>
                      <div className="text-2xl font-semibold text-navy mt-2">{d.count.toLocaleString("es-MX")}</div>
                    </button>
                  ); })}
                </div>
              )}
              {table && (
                <div className="mt-5 overflow-auto border border-navy/10 rounded-xl max-h-[45vh]">
                  <table className="w-full text-xs" data-testid="m365-table">
                    <thead className="bg-navy/5 sticky top-0"><tr>{table.columns.map((c) => <th key={c} className="text-left px-3 py-2 font-medium text-navy/60 whitespace-nowrap">{c}</th>)}</tr></thead>
                    <tbody>{table.rows.map((r, i) => (
                      <tr key={i} className="border-t border-navy/5">{table.columns.map((c) => <td key={c} className="px-3 py-2 text-navy/80 whitespace-nowrap max-w-xs truncate">{typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c] ?? "")}</td>)}</tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>

            {/* bitácora */}
            {logs.length > 0 && (
              <div className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="m365-logs">
                <div className="flex items-center gap-2 font-medium text-navy mb-4"><ScrollText className="w-4 h-4 text-violet" /> Bitácora</div>
                <div className="space-y-2">
                  {logs.slice(0, 6).map((l) => (
                    <div key={l.id} className="flex items-center justify-between text-sm border-b border-navy/5 pb-2">
                      <span className="inline-flex items-center gap-1.5">{l.status === "ok" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-rose-500" />}<b>{l.total}</b> registros {l.sandbox ? "(sandbox)" : ""}</span>
                      <span className="text-navy/40 text-xs">{new Date(l.started_at).toLocaleString("es-MX")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </div>)}
          </motion.div>
        )}
      </div>
    </OSLayout>
  );
}
